import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { ArrowRight } from "lucide-react";
import { useQuery } from "convex/react";
import { SiteLayout } from "@/components/site/layout";
import { Reveal } from "@/components/site/reveal";
import {
  SERVICES,
  allServicePages,
  mergeLiveServices,
  servicePagePath,
  type LiveServiceRow,
  type ServicePageDef,
} from "@/lib/site";
import { api } from "@/lib/convex";
import { useInnhold } from "@/lib/innhold";
import { localBusinessJsonLd } from "@/components/site/jsonld";

const BUSINESS_ID = "jh77w8p1ewdwak4qqg7k8fjben8b8zn5";

const TITLE = "Priser | Drammen Car Wash";
const DESC = "Full prisliste for Drammen Car Wash. Se priser for alle tjenester, liten og stor bil.";

export const Route = createFileRoute("/priser")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(localBusinessJsonLd()) },
    ],
  }),
  component: PricesPage,
});

function priceCell(v: number | null, fromPrice?: boolean) {
  if (v == null) return "Etter avtale";
  return `${fromPrice ? "fra " : ""}${v} kr`;
}

function PricesPage() {
  // Tekstene eieren kan redigere fra Ascendly-appen. Se src/lib/innhold.ts.
  const t = useInnhold();
  // Live Convex service data (price, description) matched onto the local SERVICES list by
  // name. Falls back to the local static values while this query is loading.
  const liveServicesRaw = useQuery(api.bookings.listServicesForBusiness, {
    businessId: BUSINESS_ID,
  }) as LiveServiceRow[] | undefined;
  const services = useMemo(() => mergeLiveServices(SERVICES, liveServicesRaw), [liveServicesRaw]);

  /* Prislista er den andre siden som nevner hver eneste tjeneste ved navn.
     Da bor navnet peke pa siden om tjenesten, ikke bare sta som tekst. Se
     kommentaren i index.tsx for hvorfor de lenkene mangler i dag. */
  const sidePerTjeneste = useMemo(() => {
    const kart = new Map<string, ServicePageDef>();
    for (const side of allServicePages(liveServicesRaw)) {
      if (side.serviceId) kart.set(side.serviceId, side);
      if (side.dynamic) kart.set(side.preselect, side);
    }
    return kart;
  }, [liveServicesRaw]);

  return (
    <SiteLayout>
      <section className="pt-32 pb-16 sm:pt-40 sm:pb-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow">{t("priser.stikktittel")}</div>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl text-balance">{t("priser.tittel")}</h1>
            <p className="mt-4 text-muted-foreground max-w-xl">
              {t("priser.tekst")}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 sm:pb-28 md:pb-40">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-10">
          <Reveal>
            <div className="rounded-2xl border border-border overflow-hidden overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="bg-secondary/40 text-left">
                    <th className="px-5 py-4 font-medium">{t("priser.tabell.tjeneste")}</th>
                    <th className="px-5 py-4 font-medium whitespace-nowrap">{t("priser.tabell.liten")}</th>
                    <th className="px-5 py-4 font-medium whitespace-nowrap">{t("priser.tabell.stor")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {services.map((s) => (
                    <tr key={s.id} className="align-top">
                      <td className="px-5 py-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-serif text-lg">
                            {sidePerTjeneste.has(s.id) ? (
                              <Link
                                to={servicePagePath(sidePerTjeneste.get(s.id)!) as any}
                                className="transition-colors hover:text-primary"
                              >
                                {s.name}
                              </Link>
                            ) : (
                              s.name
                            )}
                          </div>
                          {s.featured && (
                            <span className="rounded-full bg-primary/15 text-primary text-[11px] px-2.5 py-0.5 tracking-wide">
                              {t("felles.mestpopulaer")}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-muted-foreground text-xs leading-relaxed max-w-sm">{s.desc}</div>
                      </td>
                      <td className="px-5 py-4 text-primary whitespace-nowrap">{priceCell(s.priceSmall, s.fromPrice)}</td>
                      <td className="px-5 py-4 text-primary whitespace-nowrap">{priceCell(s.priceLarge, s.fromPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
          <Reveal delay={100} className="mt-10 flex justify-center">
            <Link
              to="/book"
              className="inline-flex items-center gap-2 rounded-full bg-primary-strong px-7 py-3.5 text-sm font-medium text-primary-foreground shadow-luxe lift"
            >
              {t("felles.bookknapp")} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </SiteLayout>
  );
}
